import React, { useState, useEffect} from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';




const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px 50px;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    padding: 0 15px 40px;
    align-items: stretch; // Change to stretch on mobile
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #2c3e50;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #34495e;
`;

// Update the Form component to ensure it centers properly on mobile
const Form = styled.form`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const FormSection = styled.div`
  margin-bottom: 35px;
  background: rgba(255, 255, 255, 0.6);
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(179, 139, 109, 0.1);
  transition: all 0.3s ease;
  width: 100%; // Ensure full width
  box-sizing: border-box; // Include padding in width calculation
  
  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    padding: 20px 15px;
    margin-bottom: 25px;
    
    /* Disable hover effects on mobile */
    &:hover {
      transform: none;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    }
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 20px;
  font-size: 1.4rem;
  font-weight: 500;
  position: relative;
  padding-bottom: 10px;
  font-family: 'Cormorant Garamond', serif;
  text-align: center; // Center align on all devices
  
  &::after {
    content: "";
    position: absolute;
    left: 50%; // Center the underline
    bottom: 0;
    height: 1px;
    width: 100px;
    transform: translateX(-50%); // Center properly
    background: linear-gradient(to right, transparent, #d4b59e, transparent); // Updated gradient for centered look
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 15px;
  }
`;

const CenteredP = styled.p`
  text-align: center;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    margin-bottom: 15px;
    font-size: 0.95rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #5a4a42;
  font-size: 1.05rem;
`;

const Input = styled.input`
  width: 100%;
  max-width: 100%; // Add this to prevent overflow
  padding: 12px 15px;
  border: 1px solid #e0d5c5;
  border-radius: 4px;
  font-size: 16px;
  background-color: rgba(255, 255, 255, 0.8);
  color: #5a4a42;
  font-family: 'Cormorant Garamond', serif;
  transition: all 0.3s ease;
  box-sizing: border-box; // Add this to include padding in width calculation
  
  &:focus {
    outline: none;
    border-color: #b38b6d;
    box-shadow: 0 0 0 3px rgba(179, 139, 109, 0.2);
    background-color: white;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  max-width: 100%; // Ensure it doesn't overflow its container
  padding: 12px 15px;
  border: 1px solid #e0d5c5;
  border-radius: 4px;
  font-size: 16px;
  background-color: rgba(255, 255, 255, 0.8);
  color: #5a4a42;
  font-family: 'Cormorant Garamond', serif;
  transition: all 0.3s ease;
  min-height: 120px;
  max-height: 300px; // Limit maximum height
  resize: vertical;
  box-sizing: border-box; // Include padding and border in the element's total width and height
  overflow-y: auto; // Add scrollbar when content exceeds height
  overflow-x: hidden; // Hide horizontal scrollbar
  
  &:focus {
    outline: none;
    border-color: #b38b6d;
    box-shadow: 0 0 0 3px rgba(179, 139, 109, 0.2);
    background-color: white;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0d5c5;
  border-radius: 4px;
  font-size: 16px;
  background-color: rgba(255, 255, 255, 0.8);
  color: #5a4a42;
  font-family: 'Cormorant Garamond', serif;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23b38b6d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 15px;
  
  &:focus {
    outline: none;
    border-color: #b38b6d;
    box-shadow: 0 0 0 3px rgba(179, 139, 109, 0.2);
    background-color: white;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(to right, #b38b6d, #d4b59e);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(179, 139, 109, 0.3);
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: 1px;
  text-transform: uppercase;
  
  &:hover {
    background: linear-gradient(to right, #a07a5f, #c4a58e);
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(179, 139, 109, 0.4);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  margin-top: 5px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 30px;
  background: rgba(46, 204, 113, 0.1);
  border: 1px solid rgba(46, 204, 113, 0.3);
  border-radius: 8px;
  margin: 20px auto; // Changed from 20px 0 to 20px auto
  width: 100%; // Set to 100%
  max-width: 600px; // Match Form max-width
  box-sizing: border-box;
  
  h3 {
    color: #27ae60;
    margin-bottom: 15px;
  }
  
  p {
    color: #2c3e50;
    line-height: 1.6;
  }
  
  @media (max-width: 480px) {
    padding: 25px 15px;
    margin: 15px auto;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 5px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
  
  input {
    margin-right: 10px;
  }
`;

const GuestsContainer = styled.div`
  margin-top: 25px;
  border-top: 1px dashed rgba(179, 139, 109, 0.3);
  padding-top: 20px;
`;

const GuestSection = styled.div`
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid #e0d5c5;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.4);
`;

const GuestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const GuestTitle = styled.h4`
  margin: 0;
  color: #2c3e50;
  font-family: 'Cormorant Garamond', serif;
`;

const GuestInfoCard = styled.div`
  margin-bottom: 25px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  border-left: 4px solid #b38b6d;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: relative; /* Critical for absolute positioning of child elements */
  overflow: visible; /* Ensure the button isn't cut off */
`;

const GuestInfoRow = styled.div`
  display: flex;
  margin-bottom: 8px;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const GuestInfoLabel = styled.span`
  min-width: 120px;
  font-weight: 600;
  color: #5a4a42;
`;

const GuestInfoValue = styled.span`
  color: #2c3e50;
  flex: 1;
`;

const GuestInfoHeader = styled.h4`
  margin: 0 0 15px;
  color: #b38b6d;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.3rem;
  font-weight: 500;
`;
const EditableField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #b38b6d;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
  width: auto;
  min-width: 50px;
  text-align: center;
  margin-left: 10px;
  
  &:hover {
    background-color: rgba(179, 139, 109, 0.1);
    text-decoration: underline;
  }
  
  &:focus {
    outline: none;
  }
`;

const EditableValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  
  @media (max-width: 600px) {
    justify-content: space-between;
  }
`;

const AddressInputGroup = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 10px;
  margin-top: 10px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CityStateZipGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 60px 100px;
  gap: 10px;
  margin-top: 10px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr 80px 100px;
  }
`;
const SaveCancelButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(to right, #b38b6d, #d4b59e)' : '#f5f5f5'};
  color: ${props => props.primary ? 'white' : '#5a4a42'};
  border: ${props => props.primary ? 'none' : '1px solid #e0d5c5'};
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
`;

const SubmissionFeedback = styled.div`
  margin-top: 15px;
  padding: 12px;
  border-radius: 4px;
  text-align: center;
  font-size: 0.95rem;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SubmissionError = styled(SubmissionFeedback)`
  background-color: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #c0392b;
`;

const SubmissionSuccess = styled(SubmissionFeedback)`
  background-color: rgba(46, 204, 113, 0.1);
  border: 1px solid rgba(46, 204, 113, 0.3);
  color: #27ae60;
`;

const ButtonWithIcon = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  
  & svg {
    margin-left: 8px;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 20px;
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 2px solid #e0d5c5;
  margin-bottom: 20px;
  overflow-x: auto; /* Allow scrolling for many tabs */
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d4b59e;
    border-radius: 10px;
  }
`;

const Tab = styled.button.attrs({ type: 'button' })`
  padding: 10px 15px;
  background: ${props => props.active ? 'rgba(179, 139, 109, 0.1)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#b38b6d' : 'transparent'};
  margin-right: 5px;
  margin-bottom: -2px;
  color: ${props => props.active ? '#b38b6d' : '#666'};
  font-family: 'Cormorant Garamond', serif;
  font-size: 1rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: rgba(179, 139, 109, 0.05);
    color: #b38b6d;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(179, 139, 109, 0.2);
  }
`;
const BackButton = styled.button`
  background: none;
  border: none;
  color: #b38b6d;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 15px;
  transition: all 0.2s ease;
  
  svg {
    margin-right: 5px;
  }
  
  &:hover {
    color: #a07a5f;
    text-decoration: underline;
  }
  
  &:focus {
    outline: none;
  }
`;

const SummarySection = styled.div`
  margin-top: 25px;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  border: 1px solid rgba(179, 139, 109, 0.2);
`;

const SummaryTitle = styled.h4`
  font-family: 'Cormorant Garamond', serif;
  color: #5a4a42;
  font-size: 1.2rem;
  margin-bottom: 15px;
  border-bottom: 1px solid rgba(179, 139, 109, 0.2);
  padding-bottom: 8px;
`;

const SummaryList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const SummaryItem = styled.li`
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed rgba(179, 139, 109, 0.15);
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 0.95rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    margin-bottom: 10px;
  }
`;

const SummaryLabel = styled.span`
  font-weight: 600;
  color: #5a4a42;
`;

const SummaryValue = styled.span`
  color: #2c3e50;
`;

const ReturnButton = styled.button`
  background: none;
  color: #b38b6d;
  border: 2px solid #b38b6d;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 1rem;
  font-family: 'Cormorant Garamond', serif;
  cursor: pointer;
  margin-top: 25px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(179, 139, 109, 0.1);
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(179, 139, 109, 0.2);
  }
`;

const RSVP = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchEmail, setSearchEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestFound, setGuestFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [groupFormData, setGroupFormData] = useState([]);
  
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState('');

  const [editableMembers, setEditableMembers] = useState({});

  // Guest data
  const [guestData, setGuestData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    rsvp: 'not yet',
    cannot_attend: false
  });
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  });
  const [submissionStatus, setSubmissionStatus] = useState({
    success: false,
    error: false,
    message: ''
  });


  useEffect(() => {
    if (currentUser && !guestFound && !submitSuccess) {
      // Set the search email to the authenticated user's email
      const userEmail = currentUser.email;
      setSearchEmail(userEmail);
      
      // Automatically search for the guest
      const autoSearchGuest = async () => {
        setLoading(true);
        setError('');
        
        try {
          console.log(`Auto-searching for guest with email: ${userEmail}`);
          const endpoint = `https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/guest?email=${encodeURIComponent(userEmail)}`;
          console.log(`Making request to: ${endpoint}`);
          
          const response = await axios.get(endpoint);
          console.log('Response received:', response);
          
          if (response.data && response.data.primaryGuest) {
            // Set the primary guest data (same as your existing searchGuest logic)
            const primaryGuest = response.data.primaryGuest;
            
            setGuestData({
              id: primaryGuest.id.S,
              firstName: primaryGuest.firstName.S,
              lastName: primaryGuest.lastName.S,
              address_street: primaryGuest.address_street?.S || '',
              address_city: primaryGuest.address_city?.S || '',
              address_state: primaryGuest.address_state?.S || '',
              address_zip: primaryGuest.address_zip?.S || '',
              rsvp: primaryGuest.rsvp?.S || 'not yet',
              cannot_attend: primaryGuest.cannot_attend?.BOOL || false
            });
            
            // Initialize form data for primary guest (same as existing code)
            const primaryFormData = {
              attending: primaryGuest.cannot_attend?.BOOL ? 'no' : 
                        (primaryGuest.rsvp?.S === 'attending' ? 'yes' : ''),
              mealChoice: primaryGuest.mealChoice?.S || '',
              specialRequests: primaryGuest.specialRequests?.S || ''
            };
            
            // Check if this is a group (same as existing code)
            if (response.data.isGroup && response.data.groupMembers?.length > 0) {
              setIsGroup(true);
              
              // Process group members
              const members = response.data.groupMembers.map(member => ({
                id: member.id.S,
                firstName: member.firstName.S,
                lastName: member.lastName.S,
                rsvp: member.rsvp?.S || 'not yet',
                cannot_attend: member.cannot_attend?.BOOL || false,
                mealChoice: member.mealChoice?.S || '',
                specialRequests: member.specialRequests?.S || ''
              }));
              
              setGroupMembers(members);
              
              // Initialize form data for all group members
              const allFormData = [
                {...primaryFormData},
                ...members.map(member => ({
                  attending: member.cannot_attend ? 'no' : 
                            (member.rsvp === 'attending' ? 'yes' : ''),
                  mealChoice: member.mealChoice || '',
                  specialRequests: member.specialRequests || ''
                }))
              ];
              
              setGroupFormData(allFormData);
              setFormData({...primaryFormData});
            } else {
              // Single guest
              setIsGroup(false);
              setGroupMembers([]);
              setGroupFormData([{...primaryFormData}]);
              setFormData({...primaryFormData});
            }
            
            setGuestFound(true);
          } else {
            console.log('No guest found with email:', userEmail);
            setError('No guest found with your login email. Please try searching with a different email or contact the couple.');
          }
        } catch (err) {
          console.error('Error auto-fetching guest data:', err);
          
          if (err.response) {
            setError(`Server error (${err.response.status}): ${err.response.data?.message || 'Unknown error'}`);
          } else if (err.request) {
            setError('No response received from server. Please check your internet connection and try again.');
          } else {
            setError(`An error occurred: ${err.message}`);
          }
        }
        
        setLoading(false);
      };
      
      autoSearchGuest();
    }
  }, [currentUser, guestFound, submitSuccess]);


  const resetGuest = () => {
    // Reset guest-related state
    setGuestFound(false);
    setGuestData({
      id: '',
      firstName: '',
      lastName: '',
      address_street: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      rsvp: 'not yet',
      cannot_attend: false
    });
    setFormData({
      attending: '',
      mealChoice: '',
      specialRequests: ''
    });
    setGroupMembers([]);
    setIsGroup(false);
    setActiveTab(0);
    setGroupFormData([]);
    setSubmissionStatus({
      success: false,
      error: false,
      message: ''
    });
    
    // Don't reset the searchEmail so the user can make adjustments if needed
  };

  const handleEditEmail = () => {
    setEmailForm(guestData.id);
    setEditingEmail(true);
  };

  const saveEmailChanges = async () => {
    try {
      setSubmissionStatus({ success: false, error: false, message: '' });
      
      // Show a loading indicator
      const originalEmail = guestData.id;
      const newEmail = emailForm;
      
      // Validate that the new email is different and properly formatted
      if (originalEmail === newEmail) {
        setEditingEmail(false);
        return; // No change needed
      }
      
      // Use the new Lambda function to handle the email update transaction
      const response = await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/updateEmail', {
        oldEmail: originalEmail,
        newEmail: newEmail
      });
      
      // Update local state
      setGuestData(prev => ({
        ...prev,
        id: newEmail
      }));
      
      // Show success message
      setSubmissionStatus({
        success: true,
        error: false,
        message: 'Email updated successfully!'
      });
      
      // Close the editing field
      setEditingEmail(false);
      
      // Clear the message after a few seconds
      setTimeout(() => {
        setSubmissionStatus({ success: false, error: false, message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error updating email:', error);
      
      // Show error message
      setSubmissionStatus({
        success: false,
        error: true,
        message: `Failed to update email: ${error.response?.data?.message || error.message || 'Unknown error'}`
      });
      
      // Keep the field in edit mode so the user can try again
    }
  };

  // Function to cancel email editing
  const cancelEmailEdit = () => {
    setEditingEmail(false);
  };

  // Add a function to handle editing address
  const handleEditAddress = () => {
    setAddressForm({
      street: guestData.address_street,
      city: guestData.address_city,
      state: guestData.address_state,
      zip: guestData.address_zip
    });
    setEditingAddress(true);
  };
  
  // Function to handle address form changes
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Function to save address changes
  const saveAddressChanges = () => {
    setGuestData(prev => ({
      ...prev,
      address_street: addressForm.street,
      address_city: addressForm.city,
      address_state: addressForm.state,
      address_zip: addressForm.zip
    }));
    setEditingAddress(false);
  };
  
  // Function to cancel address editing
  const cancelAddressEdit = () => {
    setEditingAddress(false);
  };
  
  
  // RSVP form data
  const [formData, setFormData] = useState({
    attending: '',
    mealChoice: '',
    specialRequests: ''
  });
  
  const searchGuest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      console.log(`Searching for guest with email: ${searchEmail}`);
      const endpoint = `https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/guest?email=${encodeURIComponent(searchEmail)}`;
      console.log(`Making request to: ${endpoint}`);
      
      const response = await axios.get(endpoint);
      console.log('Response received:', response);
      
      if (response.data && response.data.primaryGuest) {
        // Set the primary guest data
        const primaryGuest = response.data.primaryGuest;
        
        setGuestData({
          id: primaryGuest.id.S,
          firstName: primaryGuest.firstName.S,
          lastName: primaryGuest.lastName.S,
          address_street: primaryGuest.address_street?.S || '',
          address_city: primaryGuest.address_city?.S || '',
          address_state: primaryGuest.address_state?.S || '',
          address_zip: primaryGuest.address_zip?.S || '',
          rsvp: primaryGuest.rsvp?.S || 'not yet',
          cannot_attend: primaryGuest.cannot_attend?.BOOL || false
        });
        
        // Initialize form data for primary guest
        const primaryFormData = {
          attending: primaryGuest.cannot_attend?.BOOL ? 'no' : 
                    (primaryGuest.rsvp?.S === 'attending' ? 'yes' : ''),
          mealChoice: primaryGuest.mealChoice?.S || '',
          specialRequests: primaryGuest.specialRequests?.S || ''
        };
        
        // Check if this is a group
        if (response.data.isGroup && response.data.groupMembers?.length > 0) {
          setIsGroup(true);
          
          // Process group members
          const members = response.data.groupMembers.map(member => ({
            id: member.id.S,
            firstName: member.firstName.S,
            lastName: member.lastName.S,
            rsvp: member.rsvp?.S || 'not yet',
            cannot_attend: member.cannot_attend?.BOOL || false,
            mealChoice: member.mealChoice?.S || '',
            specialRequests: member.specialRequests?.S || ''
          }));
          
          setGroupMembers(members);
          
          // Initialize form data for all group members with INDEPENDENT objects
          const allFormData = [
            {...primaryFormData},  // Use spread operator to create a new object
            ...members.map(member => ({
              attending: member.cannot_attend ? 'no' : 
                        (member.rsvp === 'attending' ? 'yes' : ''),
              mealChoice: member.mealChoice || '',
              specialRequests: member.specialRequests || ''
            }))
          ];
          
          setGroupFormData(allFormData);
          setFormData({...primaryFormData}); // Set active tab form data with a copy
        } else {
          // Single guest
          setIsGroup(false);
          setGroupMembers([]);
          setGroupFormData([{...primaryFormData}]); // Use spread operator to create a new object
          setFormData({...primaryFormData}); // Set a copy for the form data
        }
        
        setGuestFound(true);
      } else {
        console.log('No guest found with email:', searchEmail);
        setError('No guest found with that email. Please try again or contact the couple.');
      }
    } catch (err) {
      console.error('Error fetching guest data:', err);
      
      if (err.response) {
        setError(`Server error (${err.response.status}): ${err.response.data?.message || 'Unknown error'}`);
      } else if (err.request) {
        setError('No response received from server. Please check your internet connection and try again.');
      } else {
        setError(`An error occurred: ${err.message}`);
      }
    }
    
    setLoading(false);
  };
  
  // Handle tab changes
  const handleTabChange = (index, e) => {
    // Explicitly prevent default form behavior if event exists
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Save current tab data to group form data
    const updatedGroupFormData = [...groupFormData];
    updatedGroupFormData[activeTab] = {...formData};
    
    // Switch to new tab
    setActiveTab(index);
    
    // Load the selected tab's form data
    // If no data exists for this tab yet, create an empty object with default values
    const nextTabData = updatedGroupFormData[index] || { 
      attending: '', 
      mealChoice: '', 
      specialRequests: '' 
    };
    
    // Update form data with the selected tab's data
    setFormData({...nextTabData});
    
    // Update group form data array
    setGroupFormData(updatedGroupFormData);
    
    // Return false to further ensure no form submission
    return false;
  };
  
  // Update form data for the current tab
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Create updated form data for the current tab only
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // Handle special cases like clearing meal choice when declining
    if (name === 'attending' && value === 'no') {
      updatedFormData.mealChoice = '';
    }
    
    // Set the form data for the current tab only
    setFormData(updatedFormData);
    
    // Create a COPY of the existing group form data array
    const updatedGroupFormData = [...groupFormData];
    
    // Update ONLY the current tab's data in the array
    updatedGroupFormData[activeTab] = updatedFormData;
    
    // Set the updated group form data
    setGroupFormData(updatedGroupFormData);
  };
  
  // Handle RSVP submission for the entire group
  const handleSubmitRSVP = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSubmissionStatus({ success: false, error: false, message: '' });
    
    try {
      // If it's a group, submit RSVP for all members
      if (isGroup) {
        // Primary guest data
        const primaryData = {
          id: guestData.id,
          firstName: guestData.firstName,
          lastName: guestData.lastName,
          address_street: guestData.address_street,
          address_city: guestData.address_city,
          address_state: guestData.address_state,
          address_zip: guestData.address_zip,
          timestamp: new Date().toISOString(),
          firstName: guestData.firstName,
          lastName: guestData.lastName
        };
        if (groupFormData[0].attending === 'yes') {
          primaryData.rsvp = 'attending';
          primaryData.cannot_attend = false;
          primaryData.mealChoice = groupFormData[0].mealChoice;
          primaryData.specialRequests = groupFormData[0].specialRequests;

        } else if (groupFormData[0].attending === 'no') {
          primaryData.rsvp = 'declined';
          primaryData.cannot_attend = true;
          // Clear meal choice and special requests for declined guests
          primaryData.mealChoice = '';
          primaryData.specialRequests = '';
        }        
        // Submit primary guest RSVP
        await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/respondrsvp', primaryData);
        
        // Submit RSVP for each group member
        for (let i = 0; i < groupMembers.length; i++) {
          const memberData = {
            id: groupMembers[i].id,
            timestamp: new Date().toISOString(),
            firstName: groupMembers[i].firstName,
            lastName: groupMembers[i].lastName
          };
          // Only include RSVP fields if a selection was made
          if (groupFormData[i + 1]?.attending === 'yes') {
            memberData.rsvp = 'attending';
            memberData.cannot_attend = false;
            memberData.mealChoice = groupFormData[i + 1].mealChoice;
            memberData.specialRequests = groupFormData[i + 1].specialRequests;
          } else if (groupFormData[i + 1]?.attending === 'no') {
            memberData.rsvp = 'declined';
            memberData.cannot_attend = true;
            // Clear meal choice and special requests for declined guests
            memberData.mealChoice = '';
            memberData.specialRequests = '';
          }          
          await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/respondrsvp', memberData);
        }
        try {
          await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/sendconfirmation', {
            primaryGuestEmail: guestData.id,
            timestamp: new Date().toISOString()
          });
          console.log('Confirmation email request sent successfully');
        } catch (confirmErr) {
          console.error('Error sending confirmation email request:', confirmErr);
          // Don't show an error to the user, this is a secondary action
        }
      } else {
        // Single guest RSVP
        const updateData = {
          id: guestData.id,
          firstName: guestData.firstName,
          lastName: guestData.lastName,
          address_street: guestData.address_street,
          address_city: guestData.address_city,
          address_state: guestData.address_state,
          address_zip: guestData.address_zip,
          timestamp: new Date().toISOString()
        };

        // Only include RSVP fields if a selection was made
        if (formData.attending === 'yes') {
          updateData.rsvp = 'attending';
          updateData.cannot_attend = false;
          updateData.mealChoice = formData.mealChoice;
          updateData.specialRequests = formData.specialRequests;
        } else if (formData.attending === 'no') {
          updateData.rsvp = 'declined';
          updateData.cannot_attend = true;
          // Clear meal choice and special requests for declined guests
          updateData.mealChoice = '';
          updateData.specialRequests = '';
      }        
        await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/respondrsvp', updateData);
        try {
          await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/sendconfirmation', {
            primaryGuestEmail: guestData.id,
            timestamp: new Date().toISOString()
          });
          console.log('Confirmation email request sent successfully');
        } catch (confirmErr) {
          console.error('Error sending confirmation email request:', confirmErr);
          // Don't show an error to the user, this is a secondary action
        }
      }
      
      // Show success message
      setSubmissionStatus({
        success: true,
        error: false,
        message: 'Your RSVP has been submitted successfully!'
      });
      
      // Delay before showing the full success page
      setTimeout(() => {
        setSubmitSuccess(true);
      }, 2000);
      
    } catch (err) {
      console.error('Error updating RSVP:', err);
      
      let errorMessage = 'An error occurred while submitting your RSVP.';
      
      if (err.response) {
        errorMessage += ` (Error ${err.response.status}: ${err.response.data?.message || 'Unknown error'})`;
      } else if (err.request) {
        errorMessage += ' No response received from the server.';
      } else {
        errorMessage += ` ${err.message}`;
      }
      
      setSubmissionStatus({
        success: false,
        error: true,
        message: errorMessage
      });
    }
    
    setSubmitting(false);
  };
  
  // From line 781 to the end

  return (
    <>
      <Helmet>
        <title>RSVP | Erika and Andrew's Wedding</title>
      </Helmet>
      <PageContainer>
        <PageTitle>
          RSVP
          <NameSpan>Erika & Andrew</NameSpan>
        </PageTitle>
        
        {!guestFound && !submitSuccess && (
          <FormSection>
            <SectionTitle>Find Your Invitation</SectionTitle>
            {currentUser ? (
              <p>Searching for your invitation using your login email ({currentUser.email})...</p>
            ) : (
              <p>Please enter the email address you provided when you received your Save the Date.</p>
            )}
            
            {loading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center' 
              }}>
                <div style={{
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #947060',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '15px'
                }} />
                <p>Loading your RSVP information...</p>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            ) : (
              <Form onSubmit={searchGuest}>
                <FormGroup>
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    type="email" 
                    id="email" 
                    placeholder={currentUser ? currentUser.email : "your.email@example.com"}
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    required
                  />
                </FormGroup>
                
                <Button type="submit" disabled={loading}>
                  {loading ? 'Searching...' : 'Find My Invitation'}
                </Button>
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </Form>
            )}
          </FormSection>
        )}
        
        {guestFound && !submitSuccess && (
          <Form onSubmit={handleSubmitRSVP}>
            {/* <BackButton type="button" onClick={resetGuest} aria-label="Go back to search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to search
            </BackButton> */}
            <FormSection>
              <SectionTitle>Welcome, {guestData.firstName}!</SectionTitle>
              {isGroup ? (
                <p>We're excited to receive your group's RSVP for our wedding on October 25, 2025.</p>
              ) : (
                <p>We're excited to receive your RSVP for our wedding on October 25, 2025.</p>
              )}
              
              {/* Guest Information Card */}
              <GuestInfoCard>
                <GuestInfoHeader>
                  {activeTab === 0 ? 'Your Information' : `${groupMembers[activeTab - 1].firstName}'s Information`}
                </GuestInfoHeader>
                
                <GuestInfoRow>
                  <GuestInfoLabel>Name:</GuestInfoLabel>
                  <GuestInfoValue>
                    {activeTab === 0 
                      ? `${guestData.firstName} ${guestData.lastName}` 
                      : `${groupMembers[activeTab - 1].firstName} ${groupMembers[activeTab - 1].lastName}`}
                  </GuestInfoValue>
                </GuestInfoRow>
                
                {/* Show address only for primary guest (tab 0) */}
                  <GuestInfoRow>
                    <GuestInfoLabel>Address:</GuestInfoLabel>
                    {editingAddress ? (
                      <EditableField>
                        <Input 
                          type="text"
                          name="street"
                          placeholder="Street Address"
                          value={addressForm.street}
                          onChange={handleAddressChange}
                        />
                        
                        <CityStateZipGroup>
                          <Input 
                            type="text"
                            name="city"
                            placeholder="City"
                            value={addressForm.city}
                            onChange={handleAddressChange}
                          />
                          <Input 
                            type="text"
                            name="state"
                            placeholder="State"
                            value={addressForm.state}
                            onChange={handleAddressChange}
                            maxLength={2}
                          />
                          <Input 
                            type="text"
                            name="zip"
                            placeholder="ZIP Code"
                            value={addressForm.zip}
                            onChange={handleAddressChange}
                          />
                        </CityStateZipGroup>
                        
                        <SaveCancelButtons>
                          <ActionButton type="button" onClick={cancelAddressEdit}>Cancel</ActionButton>
                          <ActionButton type="button" primary onClick={saveAddressChanges}>Save</ActionButton>
                        </SaveCancelButtons>
                      </EditableField>
                    ) : (
                      <EditableValue>
                        <GuestInfoValue>
                          {guestData.address_street ? (
                            `${guestData.address_street}, ${guestData.address_city}, ${guestData.address_state} ${guestData.address_zip}`
                          ) : (
                            <span style={{ fontStyle: 'italic', color: '#999' }}>No address provided</span>
                          )}
                        </GuestInfoValue>
                        <EditButton type="button" onClick={handleEditAddress}>
                          Edit
                        </EditButton>
                      </EditableValue>
                    )}
                  </GuestInfoRow>
              
                
                {/* Email row for all guests, with edit for primary guest */}
                <GuestInfoRow>
                  <GuestInfoLabel>Email:</GuestInfoLabel>
                  {activeTab === 0 ? (
                    // Primary guest email (editable)
                    editingEmail ? (
                      <EditableField>
                        <Input 
                          type="email"
                          name="email"
                          placeholder="Email Address"
                          value={emailForm}
                          onChange={(e) => setEmailForm(e.target.value)}
                        />
                        
                        <SaveCancelButtons>
                          <ActionButton type="button" onClick={cancelEmailEdit}>Cancel</ActionButton>
                          <ActionButton type="button" primary onClick={saveEmailChanges}>Save</ActionButton>
                        </SaveCancelButtons>
                      </EditableField>
                    ) : (
                      <EditableValue>
                        <GuestInfoValue>{guestData.id}</GuestInfoValue>
                        {/* <EditButton type="button" onClick={handleEditEmail}>
                          Edit
                        </EditButton> */}
                      </EditableValue>
                    )
                  ) : (
                    // Group members are also editable now
                    editableMembers[activeTab] ? (
                      <EditableField>
                        <Input 
                          type="email"
                          name="memberEmail"
                          placeholder="Email Address"
                          value={editableMembers[activeTab].email || groupMembers[activeTab - 1].id}
                          onChange={(e) => setEditableMembers({
                            ...editableMembers,
                            [activeTab]: { ...editableMembers[activeTab], email: e.target.value }
                          })}
                        />
                        
                        <SaveCancelButtons>
                          <ActionButton type="button" onClick={() => setEditableMembers({
                            ...editableMembers,
                            [activeTab]: null
                          })}>
                            Cancel
                          </ActionButton>
                          <ActionButton type="button" primary onClick={async () => {
                            try {
                              const originalEmail = groupMembers[activeTab - 1].id;
                              const newEmail = editableMembers[activeTab].email;
                              
                              // Validate that the new email is different
                              if (originalEmail === newEmail) {
                                setEditableMembers({
                                  ...editableMembers,
                                  [activeTab]: null
                                });
                                return; // No change needed
                              }
                              
                              // Use the new Lambda function to handle the group member email update
                              const response = await axios.post('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/updateEmail', {
                                oldEmail: originalEmail,
                                newEmail: newEmail
                              });
                              
                              // Update the member's email in local state
                              const updatedMembers = [...groupMembers];
                              updatedMembers[activeTab - 1] = {
                                ...updatedMembers[activeTab - 1],
                                id: newEmail
                              };
                              setGroupMembers(updatedMembers);
                              
                              // Close the editing field
                              setEditableMembers({
                                ...editableMembers,
                                [activeTab]: null
                              });
                              
                              // Show success message
                              setSubmissionStatus({
                                success: true,
                                error: false,
                                message: `${groupMembers[activeTab - 1].firstName}'s email updated successfully!`
                              });
                              
                              // Clear the message after a few seconds
                              setTimeout(() => {
                                setSubmissionStatus({ success: false, error: false, message: '' });
                              }, 3000);
                            } catch (error) {
                              console.error('Error updating member email:', error);
                              
                              // Show error message
                              setSubmissionStatus({
                                success: false,
                                error: true,
                                message: `Failed to update email: ${error.response?.data?.message || error.message || 'Unknown error'}`
                              });
                              
                              // Keep the field in edit mode so the user can try again
                            }
                          }}>
                            Save
                          </ActionButton>
                        </SaveCancelButtons>
                      </EditableField>
                    ) : (
                      <EditableValue>
                        <GuestInfoValue>{groupMembers[activeTab - 1].id}</GuestInfoValue>
                        {/* <EditButton type="button" onClick={() => setEditableMembers({
                          ...editableMembers,
                          [activeTab]: { email: groupMembers[activeTab - 1].id }
                        })}>
                          Edit
                        </EditButton> */}
                      </EditableValue>
                    )
                  )}
                </GuestInfoRow>
                
                <GuestInfoRow>
                  <GuestInfoLabel>RSVP Status:</GuestInfoLabel>
                  <GuestInfoValue>
                    {activeTab === 0 
                      ? (formData.attending === 'yes' ? 'Attending' : formData.attending === 'no' ? 'Not Attending' : 'Not Responded')
                      : (groupFormData[activeTab]?.attending === 'yes' ? 'Attending' : 
                        groupFormData[activeTab]?.attending === 'no' ? 'Not Attending' : 'Not Responded')}
                  </GuestInfoValue>
                </GuestInfoRow>
                
                {(activeTab === 0 && formData.attending === 'yes') || 
                (activeTab > 0 && groupFormData[activeTab]?.attending === 'yes') ? (
                  <GuestInfoRow>
                    <GuestInfoLabel>Meal Choice:</GuestInfoLabel>
                    <GuestInfoValue>
                      {activeTab === 0 
                        ? (formData.mealChoice === 'beef_salmon' ? 'Surf & Turf' : 
                          formData.mealChoice === 'vegetarian' ? 'Vegetarian' : 
                          formData.mealChoice === 'kids' ? 'Kids Meal' : 'Not Selected')
                        : (groupFormData[activeTab]?.mealChoice === 'beef_salmon' ? 'Surf & Turf' : 
                          groupFormData[activeTab]?.mealChoice === 'vegetarian' ? 'Vegetarian' : 
                          groupFormData[activeTab]?.mealChoice === 'kids' ? 'Kids Meal' : 'Not Selected')}
                    </GuestInfoValue>
                  </GuestInfoRow>
                ) : null}
              </GuestInfoCard>
              
              {/* Add tabs for group members */}
              {isGroup && (
                <TabContainer>
                  <TabList>
                  <Tab 
                    active={activeTab === 0} 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTabChange(0, e);
                    }}
                  >
                    {guestData.firstName}
                  </Tab>  
                    
                  {groupMembers.map((member, index) => (
                    <Tab 
                      key={member.id} 
                      active={activeTab === index + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleTabChange(index + 1, e);
                      }}
                    >
                      {member.firstName}
                    </Tab>
                  ))}
                  </TabList>
                  
                  <div style={{ marginBottom: '20px', fontSize: '0.9rem', color: '#666' }}>
                    {activeTab === 0 ? (
                      <p>You're submitting RSVP for <strong>{guestData.firstName} {guestData.lastName}</strong>. Use the tabs above to switch between group members.</p>
                    ) : (
                      <p>You're submitting RSVP for <strong>{groupMembers[activeTab - 1].firstName} {groupMembers[activeTab - 1].lastName}</strong>. Use the tabs above to switch between group members.</p>
                    )}
                  </div>
                </TabContainer>
              )}
                
              {/* RSVP Form Fields */}
              <FormGroup>
                <Label>Will {isGroup ? (activeTab === 0 ? `${guestData.firstName}` : `${groupMembers[activeTab - 1].firstName}`) : 'you'} be attending?</Label>
                <RadioGroup>
                  <RadioOption>
                    <input 
                      type="radio" 
                      name="attending" 
                      value="yes" 
                      checked={formData.attending === 'yes'}
                      onChange={handleFormChange}
                      required
                    />
                    Joyfully Accept
                  </RadioOption>
                  <RadioOption>
                    <input 
                      type="radio" 
                      name="attending" 
                      value="no" 
                      checked={formData.attending === 'no'}
                      onChange={handleFormChange}
                    />
                    Regretfully Decline
                  </RadioOption>
                </RadioGroup>
              </FormGroup>
            </FormSection>
            
            {formData.attending === 'yes' && (
              <FormSection>
                <SectionTitle>Meal Selection</SectionTitle>
                <p>Please select {isGroup ? (activeTab === 0 ? `${guestData.firstName}'s` : `${groupMembers[activeTab - 1].firstName}'s`) : 'your'} entrée preference:</p>
                
                <FormGroup>
                  <Label htmlFor="mealChoice">Meal Option</Label>
                  <Select 
                    id="mealChoice" 
                    name="mealChoice"
                    value={formData.mealChoice}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Please select...</option>
                    <option value="beef_salmon">Surf & Turf (Short Ribs and Salmon)</option>
                    <option value="vegetarian">Vegetarian</option>
                    <option value="kids">Kids Meal (12 and under)</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="specialRequests">Special Requests or Dietary Restrictions</Label>
                  <TextArea 
                    id="specialRequests"
                    name="specialRequests"
                    placeholder="Please let us know of any allergies or special accommodations..."
                    value={formData.specialRequests}
                    onChange={handleFormChange}
                  />
                </FormGroup>
              </FormSection>
            )}
            
            {/* Submit button with feedback */}
            <ButtonWithIcon type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  Submitting... 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="31.42 31.42" strokeLinecap="round">
                      <animateTransform
                        attributeName="transform"
                        attributeType="XML"
                        type="rotate"
                        from="0 12 12"
                        to="360 12 12"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </>
              ) : submissionStatus.success ? (
                <>
                  Received!
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </>
              ) : (
                'Submit RSVP'
              )}
            </ButtonWithIcon>
            
            {isGroup && !submitting && !submissionStatus.success && (
              <div style={{textAlign: 'center', marginTop: '10px', fontSize: '0.9rem', color: '#666'}}>
                <p>Submitting will record RSVP information for all group members.</p>
              </div>
            )}
            
            {submissionStatus.error && (
              <SubmissionError role="alert">
                <strong>Error:</strong> {submissionStatus.message}
              </SubmissionError>
            )}
            
            {submissionStatus.success && !submitSuccess && (
              <SubmissionSuccess role="status">
                {submissionStatus.message}
              </SubmissionSuccess>
            )}
          </Form>
        )}

        {submitSuccess && (
          <SuccessMessage>
            <h3>Thank You!</h3>
            {isGroup ? (
              <p>We've recorded the RSVP for your entire party. Thank you for taking the time to respond!</p>
            ) : (
              formData.attending === 'yes' ? (
                <p>We're so excited that you'll be joining us on our special day! Your RSVP has been recorded successfully.</p>
              ) : (
                <p>Thank you for letting us know. While we're sad you can't make it, we appreciate your response.</p>
              )
            )}
            
            <SummarySection>
              <SummaryTitle>Submission Summary</SummaryTitle>
              
              {isGroup ? (
                <SummaryList>
                  {/* Primary guest summary */}
                  <SummaryItem>
                    <SummaryRow>
                      <SummaryLabel>{guestData.firstName} {guestData.lastName} (Primary)</SummaryLabel>
                      <SummaryValue>
                        {groupFormData[0].attending === 'yes' ? 'Attending' : 
                        groupFormData[0].attending === 'no' ? 'Not Attending' : 
                        'Undecided'}
                      </SummaryValue>
                    </SummaryRow>
                    
                    {groupFormData[0].attending === 'yes' && (
                      <>
                        <SummaryRow>
                          <SummaryLabel>Meal Choice:</SummaryLabel>
                          <SummaryValue>
                            {groupFormData[0].mealChoice === 'beef_salmon' ? 'Surf & Turf (Beef and Salmon)' : 
                            groupFormData[0].mealChoice === 'vegetarian' ? 'Vegetarian' : 
                            groupFormData[0].mealChoice === 'kids' ? 'Kids Meal' : 'Not Selected'}
                          </SummaryValue>
                        </SummaryRow>
                        
                        {groupFormData[0].specialRequests && (
                          <SummaryRow>
                            <SummaryLabel>Special Requests:</SummaryLabel>
                            <SummaryValue>{groupFormData[0].specialRequests}</SummaryValue>
                          </SummaryRow>
                        )}
                      </>
                    )}
                  </SummaryItem>
                  
                  {/* Group members summaries */}
                  {groupMembers.map((member, index) => (
                    <SummaryItem key={member.id}>
                      <SummaryRow>
                        <SummaryLabel>{member.firstName} {member.lastName}</SummaryLabel>
                        <SummaryValue>
                          {groupFormData[index + 1]?.attending === 'yes' ? 'Attending' : 
                          groupFormData[index + 1]?.attending === 'no' ? 'Not Attending' : 
                          'Undecided'}
                        </SummaryValue>
                      </SummaryRow>
                      
                      {groupFormData[index + 1]?.attending === 'yes' && (
                        <>
                          <SummaryRow>
                            <SummaryLabel>Meal Choice:</SummaryLabel>
                            <SummaryValue>
                              {groupFormData[index + 1]?.mealChoice === 'beef_salmon' ? 'Surf & Turf (Beef and Salmon)' : 
                              groupFormData[index + 1]?.mealChoice === 'vegetarian' ? 'Vegetarian' : 
                              groupFormData[index + 1]?.mealChoice === 'kids' ? 'Kids Meal' : 'Not Selected'}
                            </SummaryValue>
                          </SummaryRow>
                          
                          {groupFormData[index + 1]?.specialRequests && (
                            <SummaryRow>
                              <SummaryLabel>Special Requests:</SummaryLabel>
                              <SummaryValue>{groupFormData[index + 1]?.specialRequests}</SummaryValue>
                            </SummaryRow>
                          )}
                        </>
                      )}
                    </SummaryItem>
                  ))}
                </SummaryList>
              ) : (
                <SummaryList>
                  <SummaryItem>
                    <SummaryRow>
                      <SummaryLabel>{guestData.firstName} {guestData.lastName}</SummaryLabel>
                      <SummaryValue>
                        {formData.attending === 'yes' ? 'Attending' : 
                        formData.attending === 'no' ? 'Not Attending' : 
                        'Undecided'}
                      </SummaryValue>
                    </SummaryRow>
                    
                    {formData.attending === 'yes' && (
                      <>
                        <SummaryRow>
                          <SummaryLabel>Meal Choice:</SummaryLabel>
                          <SummaryValue>
                            {formData.mealChoice === 'beef_salmon' ? 'Surf & Turf (Beef and Salmon)' : 
                            formData.mealChoice === 'vegetarian' ? 'Vegetarian' : 
                            formData.mealChoice === 'kids' ? 'Kids Meal' : 'Not Selected'}
                          </SummaryValue>
                        </SummaryRow>
                        
                        {formData.specialRequests && (
                          <SummaryRow>
                            <SummaryLabel>Special Requests:</SummaryLabel>
                            <SummaryValue>{formData.specialRequests}</SummaryValue>
                          </SummaryRow>
                        )}
                      </>
                    )}
                  </SummaryItem>
                </SummaryList>
              )}
            </SummarySection>
            
            <CenteredP style={{ marginTop: '30px' }}>
              We look forward to celebrating with you on October 25, 2025!
            </CenteredP>
            
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <ReturnButton 
                type="button" 
                onClick={() => {
                  // Reset all form state
                  setGuestFound(false);
                  setSubmitSuccess(false);
                  setSearchEmail('');
                  setFormData({ attending: '', mealChoice: '', specialRequests: '' });
                  setGroupFormData([]);
                  setActiveTab(0);
                  setSubmissionStatus({ success: false, error: false, message: '' });
                  navigate('/liveodds');
                }}
              >
                View Wedding Polls
              </ReturnButton>
            </div>
          </SuccessMessage>
        )}
      </PageContainer>
    </>
  );
};

export default RSVP;